import { cn } from '@/lib/cn';

export interface TabItem {
  id: string;
  label: string;
  /** Rendered as a count pill beside the label. */
  count?: number;
}

export interface TabsProps {
  tabs: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
}

/**
 * An underlined tab bar. Arrow keys move between tabs, matching the ARIA
 * tabs pattern; the panel itself is rendered by the caller.
 */
export function Tabs({ tabs, activeId, onChange, className }: TabsProps) {
  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    const currentIndex = tabs.findIndex((tab) => tab.id === activeId);

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const delta = event.key === 'ArrowRight' ? 1 : -1;
      const next = tabs[(currentIndex + delta + tabs.length) % tabs.length];
      if (next) {
        onChange(next.id);
      }
    }
  }

  return (
    <div
      role="tablist"
      onKeyDown={onKeyDown}
      className={cn('flex items-center gap-1 border-b border-border', className)}
    >
      {tabs.map((tab) => {
        const active = tab.id === activeId;

        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={active}
            aria-controls={`${tab.id}-panel`}
            tabIndex={active ? 0 : -1}
            onClick={() => {
              onChange(tab.id);
            }}
            className={cn(
              'relative -mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors',
              active
                ? 'border-primary font-medium text-content'
                : 'border-transparent text-content-muted hover:text-content',
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={cn(
                  'rounded-xs border px-1 text-2xs font-bold',
                  active
                    ? 'border-primary/30 bg-primary-soft text-primary'
                    : 'border-border bg-surface-muted text-content-muted',
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
